import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Autocomplete, TextField, CircularProgress, Chip } from '@mui/material';
import { getMasterOptions } from '../../services/platform/masterService';
import {
  formatCommaSeparatedMasterCodes,
  parseCommaSeparatedMasterCodes,
} from '../../constants/transportSubserviceMasters';

export interface MasterOption {
  _id: string;
  code: string;
  name: string;
}

interface Props {
  label: string;
  masterType: string;
  value: string;
  onChange: (codes: string) => void;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
  errorText?: string;
}

export default function SearchableMasterMultiSelect({
  label,
  masterType,
  value,
  onChange,
  required,
  disabled,
  helperText,
  error,
  errorText,
}: Props) {
  const { data: options = [], isLoading } = useQuery({
    queryKey: ['master-options', masterType],
    queryFn: () => getMasterOptions(masterType),
    enabled: !!masterType,
  });

  const selectedCodes = useMemo(() => parseCommaSeparatedMasterCodes(value), [value]);

  const selected = useMemo(
    () => options.filter((option) => selectedCodes.includes(option.code.toUpperCase())),
    [options, selectedCodes],
  );

  return (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      disabled={disabled || isLoading}
      options={options}
      value={selected}
      getOptionLabel={(option) => `${option.name} (${option.code})`}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      onChange={(_, opts) => onChange(formatCommaSeparatedMasterCodes(opts.map((opt) => opt.code)))}
      renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          key={option.code}
          size="small"
          label={`${option.name} (${option.code})`}
        />
      ))}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error || !!errorText}
          helperText={
            errorText
            || helperText
            || (isLoading ? 'Loading options…' : 'Select one or more from the list')
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={isLoading ? 'Loading…' : `No ${label.toLowerCase()} — add trip types first`}
    />
  );
}
